import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { AITeamMember, Task, Feature, FeatureCategory } from '../types';
import { getTeamMembers, createTask, startTask, getNameSuggestions, getFeaturesByCategory, getAvailableModels } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { useIdentity } from '../context/IdentityContext';
import TaskDetail from '../components/TaskDetail';

// Mirrors the title the quicktask endpoint derives from its prompt (routes.py
// execute_quicktask), so a task created here without a title looks the same as
// one created via Quick Task: the first line of the description, trimmed to 100
// characters.
const deriveTitleFromDescription = (description: string): string => {
  const firstLine = description.trim().split('\n')[0].trim() || description.trim();
  return firstLine.length > 100 ? firstLine.slice(0, 100) + '...' : firstLine;
};

const CreateTask: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { theme, isDark } = useTheme();
  const { currentPerson } = useIdentity();
  const [teamMembers, setTeamMembers] = useState<AITeamMember[]>([]);
  const [categories, setCategories] = useState<FeatureCategory[]>([]);
  const [featuresByCategory, setFeaturesByCategory] = useState<{ [key: string]: Feature[] }>({});
  // Task creation/intake is attributed to the GLOBAL identity by default (the navbar
  // "who am I"); the field stays editable for the rare cross-attribution case.
  const [formData, setFormData] = useState({
    ai_team_member: '',
    feature_id: '',
    title: '',
    description: '',
    created_by: currentPerson as string,
    model: '',
  });

  // Keep created_by in sync with the global identity until the user edits it by hand.
  const createdByTouched = useRef(false);
  useEffect(() => {
    if (!createdByTouched.current) {
      setFormData((prev) => ({ ...prev, created_by: currentPerson }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPerson]);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [createdTask, setCreatedTask] = useState<Task | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [nameSuggestions, setNameSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [members, featuresData, models] = await Promise.all([
        getTeamMembers(),
        getFeaturesByCategory(),
        getAvailableModels(),
      ]);
      setTeamMembers(members);
      setCategories(featuresData.categories);
      setFeaturesByCategory(featuresData.features);
      setAvailableModels(models);

      // Pre-select team member from URL if provided
      const memberFromUrl = searchParams.get('member');
      if (memberFromUrl && members.some(m => m.name === memberFromUrl)) {
        setFormData(prev => ({ ...prev, ai_team_member: memberFromUrl }));
      }

      // Pre-select feature from URL if provided
      const featureFromUrl = searchParams.get('feature');
      if (featureFromUrl) {
        setFormData(prev => ({ ...prev, feature_id: featureFromUrl }));
      }
    } catch (error: any) {
      console.error('Error loading data:', error);
      const errorMessage = error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.message
        || 'Network error - check if backend is running';
      setLoadError(`Failed to load form data: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);

    if (!formData.description.trim()) {
      setCreateError('Please describe what needs to be done.');
      return;
    }

    setIsCreating(true);
    let newTask: Task | null = null;

    try {
      // Only the description is truly required, same as Quick Task. Everything
      // else is filled in automatically here if the human left it blank, instead
      // of blocking submission on fields most quick tasks never bother setting.
      const taskPayload = {
        ...formData,
        ai_team_member: formData.ai_team_member || currentPerson,
        feature_id: formData.feature_id || undefined,
        title: formData.title.trim() || deriveTitleFromDescription(formData.description),
        created_by: formData.created_by.trim() || currentPerson,
        model: formData.model || undefined,
      };
      newTask = await createTask(taskPayload);
      setCreatedTask(newTask);
    } catch (error: any) {
      console.error('Error creating task:', error);
      const errorMessage = error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.message
        || 'Network error - check if backend is running';
      setCreateError(`Failed to create task: ${errorMessage}`);
      setIsCreating(false);
      return;
    }

    try {
      // Auto-start the task immediately
      await startTask(newTask.id);
      // Modal will now show with real-time streaming
      setIsCreating(false);
    } catch (error: any) {
      console.error('Error starting task:', error);
      // Task was created but failed to start - still show the modal
      // The user can manually start it from the task detail view
      // Extract error message from backend response if available
      const errorMessage = error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.message
        || 'Unknown error';
      setCreateError(`Task created but failed to auto-start: ${errorMessage}. You can start it manually.`);
      setIsCreating(false);
    }
  };

  const handleReturn = () => {
    const returnFeatureId = (location.state as any)?.returnToFeatureId;
    if (returnFeatureId) {
      navigate('/', { state: { openFeatureId: returnFeatureId } });
    } else {
      navigate(-1);
    }
  };

  const handleCloseModal = () => {
    setCreatedTask(null);
    setIsCreating(false);
    handleReturn();
  };

  const handleTaskUpdate = () => {
    // Task updated, can be handled here if needed
  };

  const handleNameChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    createdByTouched.current = true;
    setFormData({ ...formData, created_by: value });

    if (value.length > 0) {
      try {
        const suggestions = await getNameSuggestions(value);
        setNameSuggestions(suggestions);
        setShowSuggestions(suggestions.length > 0);
      } catch (error) {
        console.error('Error fetching name suggestions:', error);
      }
    } else {
      setShowSuggestions(false);
    }
  };

  const handleNameSelect = (name: string) => {
    createdByTouched.current = true;
    setFormData({ ...formData, created_by: name });
    setShowSuggestions(false);
  };

  const handleNameBlur = () => {
    // Delay hiding suggestions to allow click events to fire
    setTimeout(() => setShowSuggestions(false), 200);
  };

  return (
    <div style={{ backgroundColor: theme.background }}>
      {/* Action Bar */}
      <div style={{
        backgroundColor: theme.surface,
        borderBottom: `1px solid ${theme.border}`,
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <h1 style={{ margin: 0, fontSize: '18px', color: theme.text }}>Create New Task</h1>
        <button
          onClick={handleReturn}
          style={{
            padding: '8px 16px',
            backgroundColor: theme.surfaceHover,
            color: theme.text,
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          Cancel
        </button>
      </div>

      {/* Form */}
      <div style={{ maxWidth: '800px', margin: '24px auto', padding: '0 16px' }}>
        <div style={{
          backgroundColor: theme.surface,
          borderRadius: '12px',
          padding: '32px',
          boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          {isLoading && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: theme.textSecondary,
            }}>
              Loading form data...
            </div>
          )}

          {loadError && (
            <div style={{
              padding: '16px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#dc2626',
              marginBottom: '16px',
            }}>
              <strong>Error:</strong> {loadError}
              <button
                onClick={loadData}
                style={{
                  marginLeft: '12px',
                  padding: '4px 12px',
                  backgroundColor: '#dc2626',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                Retry
              </button>
            </div>
          )}

          {!isLoading && !loadError && (
          <form onSubmit={handleSubmit}>
            {/* Description leads the form, same as Quick Task: it is the only field
                you must fill in. Everything below is optional and gets filled in
                automatically from it when you submit. */}
            <div style={{ marginBottom: '8px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: theme.text,
              }}>
                Task Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what to fix or do. Everything below is optional and will be filled in for you if you leave it blank."
                rows={8}
                autoFocus
                style={{
                  width: '100%',
                  padding: '12px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
                required
              />
            </div>

            <p style={{ margin: '0 0 24px 0', fontSize: '12px', color: theme.textSecondary }}>
              This is the only required field. Title, team member, and your name below are
              auto-filled from the description and the current identity if you leave them blank.
            </p>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: theme.text,
              }}>
                Task Title <span style={{ fontSize: '12px', color: theme.textSecondary, fontWeight: 'normal' }}>(optional, auto-filled from the description)</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Leave blank to auto-generate from the description"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: theme.text,
              }}>
                AI Team Member <span style={{ fontSize: '12px', color: theme.textSecondary, fontWeight: 'normal' }}>(optional, defaults to you)</span>
              </label>
              <select
                value={formData.ai_team_member}
                onChange={(e) => setFormData({ ...formData, ai_team_member: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.surface,
                  color: theme.text,
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              >
                <option value="">Auto (defaults to {currentPerson || 'you'})</option>
                {teamMembers.map((member) => (
                  <option key={member.name} value={member.name}>
                    {member.name} - {member.role}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: theme.text,
                }}>
                  Feature <span style={{ fontSize: '12px', color: theme.textSecondary, fontWeight: 'normal' }}>(optional; pick one to include its expectations and history as context)</span>
                </label>
                <Link
                  to="/features"
                  style={{
                    fontSize: '12px',
                    color: theme.primary,
                    textDecoration: 'none',
                  }}
                >
                  Manage Features
                </Link>
              </div>
              <select
                value={formData.feature_id}
                onChange={(e) => setFormData({ ...formData, feature_id: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.surface,
                  color: theme.text,
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              >
                <option value="">No specific feature</option>
                {categories.map((category) => (
                  <optgroup key={category.id} label={category.name}>
                    {(featuresByCategory[category.id] || []).map((feature) => (
                      <option key={feature.id} value={feature.id}>
                        {feature.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {formData.feature_id && (
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: theme.textSecondary }}>
                  AI context from this feature's expectations and history will be included in the prompt.
                </p>
              )}
            </div>

            <div style={{ marginBottom: '24px', position: 'relative' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: theme.text,
              }}>
                Your Name <span style={{ fontSize: '12px', color: theme.textSecondary, fontWeight: 'normal' }}>(optional, defaults to you)</span>
              </label>
              <input
                ref={nameInputRef}
                type="text"
                value={formData.created_by}
                onChange={handleNameChange}
                onFocus={() => formData.created_by && setShowSuggestions(nameSuggestions.length > 0)}
                onBlur={handleNameBlur}
                placeholder="Enter your name"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.surface,
                  color: theme.text,
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              />
              {showSuggestions && nameSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: theme.surface,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  marginTop: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                }}>
                  {nameSuggestions.map((name, index) => (
                    <div
                      key={index}
                      onClick={() => handleNameSelect(name)}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderBottom: index < nameSuggestions.length - 1 ? `1px solid ${theme.border}` : 'none',
                        color: theme.text,
                        transition: 'background-color 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.surfaceHover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      {name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: theme.text,
              }}>
                Claude Model <span style={{ fontSize: '12px', color: theme.textSecondary, fontWeight: 'normal' }}>(optional — uses default if not set)</span>
              </label>
              <select
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.surface,
                  color: theme.text,
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              >
                <option value="">Default model</option>
                {availableModels.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            {createError && (
              <div style={{
                marginBottom: '16px',
                padding: '12px 16px',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                color: '#dc2626',
                fontSize: '14px',
              }}>
                <strong>Error:</strong> {createError}
              </div>
            )}

            <button
              type="submit"
              disabled={isCreating}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: isCreating ? '#9ca3af' : '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: isCreating ? 'not-allowed' : 'pointer',
              }}
            >
              {isCreating ? 'Creating and Starting Task...' : 'Create Task'}
            </button>
          </form>
          )}
        </div>
      </div>

      {/* Task Detail Modal for real-time streaming */}
      {createdTask && (
        <TaskDetail
          task={createdTask}
          onClose={handleCloseModal}
          onTaskUpdate={handleTaskUpdate}
        />
      )}
    </div>
  );
};

export default CreateTask;
